var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { diffComparisonType, diffComparisonValueFormat } from '@workalizer/shared/types';
import { calculateAverage, getInterval, getPercentageDiff, getPreviousPeriod, groupByPeriod } from '@workalizer/shared/utils';
import { endOfDay, startOfDay } from 'date-fns';
var RECURRING_MEETINGS = [
    { id: 901, title: 'Weekly Team Standup', durationSeconds: 1800 },
    { id: 902, title: 'Product Sync', durationSeconds: 3600 },
    { id: 903, title: 'Engineering Retro', durationSeconds: 2700 },
    { id: 904, title: 'All-Hands', durationSeconds: 2700 }
];
var CURRENT_ATTENDANCE_PATTERN = [
    [true, true, false, true],
    [true, false, true, true],
    [false, true, true, false],
    [true, true, true, false],
    [true, false, false, true],
    [true, true, true, true],
    [false, true, true, true]
];
var PREV_ATTENDANCE_PATTERN = [
    [true, false, true, false],
    [true, true, false, true],
    [true, true, true, false],
    [false, true, false, true],
    [true, false, true, true],
    [false, true, true, false],
    [true, true, false, false]
];
var buildMeetingsForPeriod = function (periodIndex, pattern) {
    return RECURRING_MEETINGS.map(function (meeting, meetingIndex) {
        var attendanceRow = pattern[periodIndex % pattern.length];
        return {
            id: meeting.id,
            title: meeting.title,
            durationSeconds: meeting.durationSeconds,
            attendance: attendanceRow[meetingIndex % attendanceRow.length]
        };
    });
};
var computeTotals = function (meetings) {
    return meetings.reduce(function (acc, meeting) {
        if (meeting.attendance) {
            acc.attendedCount++;
            acc.attendedDuration += meeting.durationSeconds;
        }
        else {
            acc.missedCount++;
            acc.missedDuration += meeting.durationSeconds;
        }
        return acc;
    }, {
        attendedCount: 0,
        attendedDuration: 0,
        missedCount: 0,
        missedDuration: 0
    });
};
var TEAM_PERIOD_DURATIONS = [
    { attendedDuration: 10800, missedDuration: 3600 },
    { attendedDuration: 9000, missedDuration: 5400 },
    { attendedDuration: 12600, missedDuration: 2700 },
    { attendedDuration: 10800, missedDuration: 4500 },
    { attendedDuration: 9000, missedDuration: 6300 },
    { attendedDuration: 12000, missedDuration: 3600 },
    { attendedDuration: 10200, missedDuration: 5400 }
];
var TEAM_USER_COUNT = 8;
export var consistencyOfAttendanceInMeets = function (input) {
    var interval = getInterval(input.dateFrom, input.dateTo);
    var dateFrom = startOfDay(input.dateFrom);
    var dateTo = endOfDay(input.dateTo);
    var _a = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
    var periods = groupByPeriod([], dateFrom, dateTo, interval);
    var previousPeriods = groupByPeriod([], startOfDay(prevPeriodDateFrom), endOfDay(prevPeriodDateTo), interval);
    var periodsData = periods.map(function (period, index) { return ({
        period: period.date,
        meetings: buildMeetingsForPeriod(index, CURRENT_ATTENDANCE_PATTERN)
    }); });
    var prevPeriodsData = previousPeriods.map(function (period, index) { return ({
        period: period.date,
        meetings: buildMeetingsForPeriod(index, PREV_ATTENDANCE_PATTERN)
    }); });
    var currentMeetings = periodsData.flatMap(function (period) { return period.meetings; });
    var prevMeetings = prevPeriodsData.flatMap(function (period) { return period.meetings; });
    var currentTotals = computeTotals(currentMeetings);
    var prevTotals = computeTotals(prevMeetings);
    var durationDiffOptions = {
        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
    };
    var teamDurationDiffOptions = __assign(__assign({}, durationDiffOptions), { comparisonType: diffComparisonType.teamAverage });
    var teamPeriods = periods.map(function (period, index) {
        var teamDurations = TEAM_PERIOD_DURATIONS[index % TEAM_PERIOD_DURATIONS.length];
        return {
            period: period.date,
            teamAverageAttended: Math.round(teamDurations.attendedDuration / TEAM_USER_COUNT),
            teamAverageSkipped: Math.round(teamDurations.missedDuration / TEAM_USER_COUNT)
        };
    });
    var teamAttendedDurationTotal = periods.reduce(function (sum, _, index) {
        var teamDurations = TEAM_PERIOD_DURATIONS[index % TEAM_PERIOD_DURATIONS.length];
        return sum + teamDurations.attendedDuration;
    }, 0);
    var teamMissedDurationTotal = periods.reduce(function (sum, _, index) {
        var teamDurations = TEAM_PERIOD_DURATIONS[index % TEAM_PERIOD_DURATIONS.length];
        return sum + teamDurations.missedDuration;
    }, 0);
    var teamAvgAttendedDuration = calculateAverage(teamAttendedDurationTotal, TEAM_USER_COUNT);
    var teamAvgMissedDuration = calculateAverage(teamMissedDurationTotal, TEAM_USER_COUNT);
    var totals = __assign(__assign({}, currentTotals), { attendedDurationPrevPeriodDiff: getPercentageDiff(prevTotals.attendedDuration, currentTotals.attendedDuration, durationDiffOptions), missedDurationPrevPeriodDiff: getPercentageDiff(prevTotals.missedDuration, currentTotals.missedDuration, durationDiffOptions), attendedDurationTeamAvgDiff: getPercentageDiff(teamAvgAttendedDuration, currentTotals.attendedDuration, teamDurationDiffOptions), missedDurationTeamAvgDiff: getPercentageDiff(teamAvgMissedDuration, currentTotals.missedDuration, teamDurationDiffOptions) });
    return {
        periods: periodsData,
        prevPeriods: prevPeriodsData,
        totals: totals,
        teamPeriods: teamPeriods
    };
};
